/**
 * Client for asab-agents' durable conversation history.
 *
 * The envelope on the wire is Go-defined and snake_case; `ChatMessage` is
 * camelCase. That conversion lives here and nowhere else. `content` and `steps`
 * are the exception: asab-agents stores them as opaque JSON, so they cross
 * untouched and `types/message.ts` remains their only schema.
 */
import apiClient from '@/lib/api/client';

import type { ChatMessage, ContentPart, ReasoningStep, Usage } from '../types/message';

const BASE = '/v1/conversations';

export type ConversationKind = 'agent' | 'playground';

/** The conversation record as asab-agents returns it. */
export interface Conversation {
  id: string;
  org_id: string;
  user_id: string;
  kind: ConversationKind;
  title: string;
  pinned?: boolean;
  ephemeral?: boolean;
  agent_id?: string;
  context_id?: string;
  model?: string;
  message_count: number;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

export interface CreateConversationRequest {
  kind: ConversationKind;
  title?: string;
  agent_id?: string;
  model?: string;
  context_id?: string;
  /** Omit to inherit the org default; the org's hard dial can still force it. */
  ephemeral?: boolean;
}

export interface UpdateConversationRequest {
  title?: string;
  pinned?: boolean;
  /** Set-once — recorded on the first agent turn to make the session resumable. */
  context_id?: string;
}

export interface ListConversationsParams {
  kind?: ConversationKind;
  agent_id?: string;
  cursor?: string;
  limit?: number;
}

interface WireUsage {
  input_tokens?: number;
  output_tokens?: number;
  duration_ms?: number;
}

interface WireMessage {
  id: string;
  seq: number;
  role: string;
  content: ContentPart[] | null;
  steps?: ReasoningStep[] | null;
  status: string;
  model?: string;
  usage?: WireUsage;
  error?: string;
  task_id?: string;
  created_at: string;
}

function toUsage(u?: WireUsage): Usage | undefined {
  if (!u) return undefined;
  return { inputTokens: u.input_tokens, outputTokens: u.output_tokens, durationMs: u.duration_ms };
}

function fromUsage(u?: Usage): WireUsage | undefined {
  if (!u) return undefined;
  return { input_tokens: u.inputTokens, output_tokens: u.outputTokens, duration_ms: u.durationMs };
}

/** Wire row → canonical message. */
export function toChatMessage(m: WireMessage): ChatMessage {
  return {
    id: m.id,
    role: m.role as ChatMessage['role'],
    content: m.content ?? [],
    // Steps cross as opaque JSON, so their timestamps arrive as ISO strings and
    // have to be revived into the Date the type promises.
    steps: m.steps?.map((s) => ({ ...s, timestamp: new Date(s.timestamp) })),
    status: m.status as ChatMessage['status'],
    model: m.model,
    usage: toUsage(m.usage),
    error: m.error,
    taskId: m.task_id,
    timestamp: new Date(m.created_at),
  };
}

export const conversationsApi = {
  async create(req: CreateConversationRequest): Promise<Conversation> {
    const { data } = await apiClient.post<Conversation>(BASE, req);
    return data;
  },

  async list(params: ListConversationsParams = {}): Promise<{
    conversations: Conversation[];
    nextCursor?: string;
  }> {
    const { data } = await apiClient.get<{
      conversations: Conversation[] | null;
      next_cursor?: string;
    }>(BASE, { params });
    return { conversations: data.conversations ?? [], nextCursor: data.next_cursor };
  },

  async get(id: string): Promise<Conversation> {
    const { data } = await apiClient.get<Conversation>(`${BASE}/${id}`);
    return data;
  },

  async update(id: string, req: UpdateConversationRequest): Promise<Conversation> {
    const { data } = await apiClient.patch<Conversation>(`${BASE}/${id}`, req);
    return data;
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async listMessages(
    id: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<{ messages: ChatMessage[]; nextCursor?: string }> {
    const { data } = await apiClient.get<{
      messages: WireMessage[] | null;
      next_cursor?: string;
    }>(`${BASE}/${id}/messages`, { params });
    return {
      messages: (data.messages ?? []).map(toChatMessage),
      nextCursor: data.next_cursor,
    };
  },

  /**
   * Appends a settled turn.
   *
   * An ephemeral conversation answers 204 with no body — the append is accepted
   * and deliberately not stored, which is a success, not an error.
   */
  async appendMessage(id: string, message: ChatMessage): Promise<ChatMessage | null> {
    const { data, status } = await apiClient.post<WireMessage | ''>(`${BASE}/${id}/messages`, {
      id: message.id,
      role: message.role,
      content: message.content,
      steps: message.steps,
      status: message.status,
      model: message.model,
      usage: fromUsage(message.usage),
      error: message.error,
      task_id: message.taskId,
    });
    if (status === 204 || !data) return null;
    return toChatMessage(data as WireMessage);
  },
};
